import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronDown, ExternalLink, Globe, Scale, Search } from 'lucide-react';
import { PageContainer, PageContent } from '@/components/ui/page-layout';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  getCompanyDetails,
  getCompanyResearchers,
} from '../services/apiService';
import type {
  CompanyDetail,
  CompanyPillar,
  MatchedResearcher,
} from '../types/api';

// ── Constants ────────────────────────────────────────────────────────────────

const RESEARCHERS_PER_PAGE = 10;

const KATPO_MAP: Record<string, string> = {
  '000': 'N/A',
  '110': '0',
  '120': '1-5',
  '130': '6-9',
  '210': '10-19',
  '220': '20-24',
  '230': '25-49',
  '240': '50-99',
  '310': '100-199',
  '320': '200-249',
  '330': '250-499',
  '340': '500-999',
  '410': '1000-1499',
  '420': '1500-1999',
  '430': '2000-2499',
  '440': '2500-2999',
  '450': '3000-3999',
  '460': '4000-4999',
  '470': '5000-9999',
  '510': '10000+',
};

function employeeRange(katpo: string | null | undefined): string | null {
  if (!katpo) return null;
  return KATPO_MAP[String(parseInt(katpo, 10))] ?? null;
}

// Scraped website URLs sometimes lack a scheme; ensure they resolve absolutely.
function ensureHttp(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

const KEYWORD_TONES: Record<
  'fundament' | 'technology' | 'opportunity',
  { label: string; chipClass: string; barClass: string }
> = {
  fundament: {
    label: 'fundament',
    chipClass:
      'bg-blue-50 text-blue-700 border-blue-200',
    barClass: 'bg-blue-700',
  },
  technology: {
    label: 'technology',
    chipClass:
      'bg-emerald-50 text-emerald-700 border-emerald-200',
    barClass: 'bg-emerald-700',
  },
  opportunity: {
    label: 'opportunity',
    chipClass:
      'bg-purple-50 text-purple-700 border-purple-200',
    barClass: 'bg-purple-700',
  },
};

type ResearcherSortKey = 'sum_score' | 'median_score' | 'name' | 'faculty';
type SortDir = 'asc' | 'desc';

// ── Page ─────────────────────────────────────────────────────────────────────

export default function CompanyDetailPage() {
  const { t, i18n } = useTranslation('companies');
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const location = useLocation();

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [researchers, setResearchers] = useState<MatchedResearcher[]>([]);
  const [researchersLoading, setResearchersLoading] = useState<boolean>(false);

  const [expandedPillars, setExpandedPillars] = useState<Record<number, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const [filterText, setFilterText] = useState<string>('');
  const [sortKey, setSortKey] = useState<ResearcherSortKey>('sum_score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState<number>(1);

  const goBack = () => {
    const from = location.state?.from;
    if (from) navigate(from);
    else navigate('/companies');
  };

  // Fetch company
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getCompanyDetails(id, i18n.language)
      .then((data) => {
        if (cancelled) return;
        if (data.result) setCompany(data.result);
        else setError(data.error || 'Company not found');
      })
      .catch((err: any) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to fetch company details');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, i18n.language]);

  // Fetch researchers
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setResearchersLoading(true);
    getCompanyResearchers(id)
      .then((data) => {
        if (cancelled) return;
        setResearchers(data.researchers || []);
      })
      .catch((err) => {
        console.error('Failed to fetch researchers:', err);
      })
      .finally(() => {
        if (!cancelled) setResearchersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Reset page when filter / sort / dataset changes
  useEffect(() => {
    setPage(1);
  }, [filterText, sortKey, sortDir, researchers.length]);

  const togglePillar = (idx: number) =>
    setExpandedPillars((p) => ({ ...p, [idx]: !p[idx] }));

  const toggleRow = (personId: number) =>
    setExpandedRows((p) => ({ ...p, [personId]: !p[personId] }));

  const handleSort = (key: ResearcherSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'sum_score' || key === 'median_score' ? 'desc' : 'asc');
    }
  };

  // ── Derived state ──────────────────────────────────────────────────────────

  const lang = i18n.language;
  const summary = company
    ? lang === 'cs'
      ? company.summary_for_embedding_cs
      : company.summary_for_embedding_en
    : null;
  const nace = company ? (lang === 'cs' ? company.nace_cz : company.nace_en) : null;
  const employees = employeeRange(company?.katpo);
  const pillarName = (p: CompanyPillar): string =>
    (lang === 'cs' ? p.pillar_name_cs : p.pillar_name_en) || '';

  const filteredResearchers = useMemo(() => {
    const q = normalize(filterText.trim());
    if (!q) return researchers;
    return researchers.filter((r) =>
      normalize(
        [r.name || '', r.faculty || '', String(r.sum_score), String(r.median_score)].join('  '),
      ).includes(q),
    );
  }, [researchers, filterText]);

  const sortedResearchers = useMemo(() => {
    const copy = [...filteredResearchers];
    copy.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      switch (sortKey) {
        case 'name':
          va = a.name.toLowerCase();
          vb = b.name.toLowerCase();
          break;
        case 'faculty':
          va = (a.faculty || '').toLowerCase();
          vb = (b.faculty || '').toLowerCase();
          break;
        case 'median_score':
          va = a.median_score;
          vb = b.median_score;
          break;
        default:
          va = a.sum_score;
          vb = b.sum_score;
      }
      const cmp =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [filteredResearchers, sortKey, sortDir]);

  const totalPages = Math.max(
    1,
    Math.ceil(sortedResearchers.length / RESEARCHERS_PER_PAGE),
  );
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * RESEARCHERS_PER_PAGE;
  const pageRows = sortedResearchers.slice(offset, offset + RESEARCHERS_PER_PAGE);

  const globalMaxPillarScore = useMemo(
    () =>
      Math.max(
        1,
        ...researchers.flatMap((r) => r.pillar_scores.map((p) => p.score)),
      ),
    [researchers],
  );

  // ── Render helpers ─────────────────────────────────────────────────────────

  const sortIndicator = (key: ResearcherSortKey) => {
    if (key !== sortKey)
      return <span className="ml-1 text-ctu-text-muted/40">↕</span>;
    return (
      <span className="ml-1 text-ctu-text-secondary">
        {sortDir === 'asc' ? '▲' : '▼'}
      </span>
    );
  };

  const headCellClass =
    'px-3 py-2.5 text-left font-bold text-xs text-ctu-text-secondary whitespace-nowrap select-none';
  const sortableHeadClass = `${headCellClass} cursor-pointer hover:bg-ctu-bg/70`;

  function paginationItems(): (number | 'ellipsis')[] {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set<number>([1, totalPages, currentPage]);
    [-1, 1].forEach((o) => set.add(currentPage + o));
    if (currentPage <= 3) [2, 3, 4].forEach((n) => set.add(n));
    if (currentPage >= totalPages - 2)
      [totalPages - 1, totalPages - 2, totalPages - 3].forEach((n) => set.add(n));
    const nums = Array.from(set)
      .filter((n) => n >= 1 && n <= totalPages)
      .sort((a, b) => a - b);
    const out: (number | 'ellipsis')[] = [];
    for (let i = 0; i < nums.length; i++) {
      out.push(nums[i]);
      if (i < nums.length - 1 && nums[i + 1] - nums[i] > 1) out.push('ellipsis');
    }
    return out;
  }

  // ── States: loading / error / not found ────────────────────────────────────

  const BackButton = (
    <Button
      variant="ghost"
      size="sm"
      onClick={goBack}
      className="mb-4 -ml-2 text-ctu-text-muted hover:text-ctu-text"
    >
      <ArrowLeft className="h-4 w-4 mr-1.5" />
      {t('detail.backToList')}
    </Button>
  );

  if (loading) {
    return (
      <PageContainer>
        <PageContent>
          {BackButton}
          <Card
            className="border-ctu-border shadow-none mb-6"
            aria-busy="true"
            aria-label={t('detail.loading')}
          >
            <CardContent className="p-8">
              <Skeleton className="h-8 w-2/3 mb-3" />
              <div className="my-6 h-px bg-border" />
              <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex flex-col gap-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-6 w-28" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="border-ctu-border shadow-none mb-6">
            <CardContent className="px-6 pt-5 pb-4 flex flex-col gap-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-3/4" />
            </CardContent>
          </Card>
          <Card className="border-ctu-border shadow-none">
            <CardContent className="px-6 pt-5 pb-4 flex flex-col gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </CardContent>
          </Card>
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageContent>
          {BackButton}
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </PageContent>
      </PageContainer>
    );
  }

  if (!company) {
    return (
      <PageContainer>
        <PageContent>
          {BackButton}
          <Alert variant="destructive">
            <AlertDescription>{t('detail.notFound')}</AlertDescription>
          </Alert>
        </PageContent>
      </PageContainer>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────

  return (
    <PageContainer>
      <PageContent>
        {BackButton}

        {/* Hero */}
        <Card className="border-ctu-border shadow-none mb-6">
          <CardContent className="p-8">
            <h1 className="text-3xl font-semibold tracking-tight text-ctu-text mb-2">
              {company.firma}
            </h1>
            {company.ico && (
              <p className="text-xs text-ctu-text-muted font-mono tabular-nums m-0">
                IČO {company.ico}
              </p>
            )}

            {(company.website || company.justice_url) && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {company.website && (
                  <a
                    href={ensureHttp(company.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-ctu-border px-3 py-1.5 text-xs font-medium text-ctu-text transition-colors hover:bg-muted/50"
                  >
                    <Globe className="h-3.5 w-3.5 text-ctu-text-muted" aria-hidden />
                    {t('detail.website')}
                    <ExternalLink className="h-3 w-3 text-ctu-text-muted" aria-hidden />
                  </a>
                )}
                {company.justice_url && (
                  <a
                    href={company.justice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-md border border-ctu-border px-3 py-1.5 text-xs font-medium text-ctu-text transition-colors hover:bg-muted/50"
                  >
                    <Scale className="h-3.5 w-3.5 text-ctu-text-muted" aria-hidden />
                    {t('detail.justiceRecord')}
                    <ExternalLink className="h-3 w-3 text-ctu-text-muted" aria-hidden />
                  </a>
                )}
              </div>
            )}

            {(nace ||
              company.obec_text ||
              employees ||
              company.founding_date ||
              company.registered_capital) && (
              <>
                <Separator className="my-6" />
                <dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
                  {nace && (
                    <div className="flex flex-col gap-1">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                        {t('detail.nace')}
                      </dt>
                      <dd className="text-sm font-medium text-ctu-text m-0">
                        {nace}
                      </dd>
                    </div>
                  )}
                  {company.obec_text && (
                    <div className="flex flex-col gap-1">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                        {t('detail.city')}
                      </dt>
                      <dd className="text-sm font-medium text-ctu-text m-0">
                        {company.obec_text}
                      </dd>
                    </div>
                  )}
                  {employees && (
                    <div className="flex flex-col gap-1">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                        {t('detail.employees')}
                      </dt>
                      <dd className="text-sm font-medium text-ctu-text m-0 tabular-nums">
                        {employees}
                      </dd>
                    </div>
                  )}
                  {company.founding_date && (
                    <div className="flex flex-col gap-1">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                        {t('detail.founded')}
                      </dt>
                      <dd className="text-sm font-medium text-ctu-text m-0">
                        {company.founding_date}
                      </dd>
                    </div>
                  )}
                  {company.registered_capital && (
                    <div className="flex flex-col gap-1">
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                        {t('detail.capital')}
                      </dt>
                      <dd className="text-sm font-medium text-ctu-text m-0 tabular-nums">
                        {company.registered_capital}
                      </dd>
                    </div>
                  )}
                </dl>
              </>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        <Card className="border-ctu-border shadow-none mb-6">
          <CardContent className="px-6 pt-5 pb-5 flex flex-col gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
              {t('detail.summary')}
            </span>
            {summary ? (
              <p className="text-sm leading-relaxed text-ctu-text m-0">
                {summary}
              </p>
            ) : (
              <p className="text-sm italic text-ctu-text-muted m-0">
                {t('detail.noSummary')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Pillars */}
        <Card className="border-ctu-border shadow-none mb-6">
          <CardContent className="px-6 pt-5 pb-5 flex flex-col gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
              {t('detail.pillars')}
            </span>

            {company.pillars && company.pillars.length > 0 ? (
              <ul className="m-0 flex flex-col gap-2 list-none p-0">
                {company.pillars.map((pillar, idx) => {
                  const open = !!expandedPillars[idx];
                  return (
                    <li
                      key={pillar.id ?? idx}
                      className="rounded-md border border-ctu-border overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => togglePillar(idx)}
                        aria-expanded={open}
                        className={cn(
                          'flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-ctu-text transition-colors hover:bg-muted/40',
                          open && 'bg-muted/30',
                        )}
                      >
                        <span>{pillarName(pillar)}</span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 shrink-0 text-ctu-text-muted transition-transform',
                            open && 'rotate-180',
                          )}
                          aria-hidden
                        />
                      </button>
                      {open && (
                        <div className="border-t border-ctu-border bg-muted/20 px-4 py-4 flex flex-col gap-4">
                          {(['fundament', 'technology', 'opportunity'] as const).map((type) => {
                            const keywords = pillar.keywords?.[type];
                            if (!keywords || keywords.length === 0) return null;
                            const tone = KEYWORD_TONES[type];
                            return (
                              <div key={type} className="flex flex-col gap-2">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                                  {t(`detail.keywordTypes.${type}`)}
                                </span>
                                <div className="flex flex-wrap gap-1.5">
                                  {keywords.map((kw, ki) => (
                                    <span
                                      key={`${type}-${ki}`}
                                      className={cn(
                                        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium',
                                        tone.chipClass,
                                      )}
                                    >
                                      {kw}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm italic text-ctu-text-muted m-0">
                {t('detail.noPillars')}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Matched Researchers */}
        <Card className="border-ctu-border shadow-none">
          <CardContent className="px-0 pt-4 pb-2 flex flex-col gap-3">
            <div className="px-6">
              <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                {t('detail.researchers.title')}
              </span>
            </div>

            {researchersLoading ? (
              <div className="px-6 flex flex-col gap-2 pb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : researchers.length === 0 ? (
              <p className="px-6 pb-4 text-sm italic text-ctu-text-muted m-0">
                {t('detail.researchers.noResults')}
              </p>
            ) : (
              <>
                {/* Filter */}
                <div className="px-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full max-w-md">
                    <Search
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ctu-text-muted"
                      aria-hidden
                    />
                    <Input
                      type="search"
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      placeholder={t('detail.researchers.filter')}
                      className="pl-8"
                    />
                  </div>
                  <span className="text-xs text-ctu-text-muted tabular-nums">
                    {researchers.length}
                    {filterText.trim() &&
                      filteredResearchers.length !== researchers.length && (
                        <> · {filteredResearchers.length}</>
                      )}
                  </span>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  <Table className="text-sm">
                    <TableHeader>
                      <TableRow className="bg-ctu-bg">
                        <TableHead className={`${headCellClass} w-12 text-right`}>
                          {t('detail.researchers.name')
                            ? '#'
                            : ''}
                        </TableHead>
                        <TableHead
                          className={`${sortableHeadClass} text-right w-24`}
                          onClick={() => handleSort('sum_score')}
                        >
                          {t('detail.researchers.score')}
                          {sortIndicator('sum_score')}
                        </TableHead>
                        <TableHead
                          className={`${sortableHeadClass} text-right w-28`}
                          onClick={() => handleSort('median_score')}
                        >
                          {t('detail.researchers.medianScore')}
                          {sortIndicator('median_score')}
                        </TableHead>
                        <TableHead
                          className={sortableHeadClass}
                          onClick={() => handleSort('name')}
                        >
                          {t('detail.researchers.name')}
                          {sortIndicator('name')}
                        </TableHead>
                        <TableHead
                          className={sortableHeadClass}
                          onClick={() => handleSort('faculty')}
                        >
                          {t('detail.researchers.faculty')}
                          {sortIndicator('faculty')}
                        </TableHead>
                        <TableHead className={`${headCellClass} w-10`} />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pageRows.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={6}
                            className="px-3 py-10 text-center text-sm text-ctu-text-muted"
                          >
                            {t('detail.researchers.noResults')}
                          </TableCell>
                        </TableRow>
                      ) : (
                        pageRows.map((r, idx) => {
                          const open = !!expandedRows[r.person_id];
                          const globalIdx = offset + idx + 1;
                          return (
                            <React.Fragment key={r.person_id}>
                              <TableRow
                                className="cursor-pointer hover:bg-muted/40"
                                onClick={() => toggleRow(r.person_id)}
                              >
                                <TableCell className="px-3 py-2.5 text-right font-mono text-xs tabular-nums text-ctu-text-muted">
                                  {globalIdx}
                                </TableCell>
                                <TableCell className="px-3 py-2.5 text-right">
                                  <Badge
                                    variant="secondary"
                                    className="font-mono tabular-nums"
                                  >
                                    {r.sum_score.toFixed(2)}
                                  </Badge>
                                </TableCell>
                                <TableCell className="px-3 py-2.5 text-right font-mono tabular-nums text-ctu-text-secondary">
                                  {r.median_score.toFixed(2)}
                                </TableCell>
                                <TableCell className="px-3 py-2.5 font-medium text-ctu-text">
                                  {r.name}
                                </TableCell>
                                <TableCell className="px-3 py-2.5 text-ctu-text-secondary">
                                  {r.faculty || '—'}
                                </TableCell>
                                <TableCell className="px-3 py-2.5 text-center">
                                  <ChevronDown
                                    className={cn(
                                      'h-4 w-4 inline-block text-ctu-text-muted transition-transform',
                                      open && 'rotate-180',
                                    )}
                                    aria-hidden
                                  />
                                </TableCell>
                              </TableRow>

                              {open && (
                                <TableRow>
                                  <TableCell
                                    colSpan={6}
                                    className="px-5 py-4 bg-muted/20 border-b border-ctu-border"
                                  >
                                    {/* Pillar scores */}
                                    {r.pillar_scores && r.pillar_scores.length > 0 && (
                                      <div className="mb-4">
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                                          {t('detail.researchers.pillarScore')}
                                        </span>
                                        <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                                          {r.pillar_scores.map((ps) => {
                                            const pct =
                                              globalMaxPillarScore > 0
                                                ? (ps.score / globalMaxPillarScore) * 100
                                                : 0;
                                            const name =
                                              lang === 'cs'
                                                ? ps.pillar_name_cs
                                                : ps.pillar_name_en;
                                            return (
                                              <div
                                                key={ps.pillar_id}
                                                title={`${name}: ${ps.score.toFixed(2)}`}
                                              >
                                                <div className="text-xs text-ctu-text-muted truncate">
                                                  {name}
                                                </div>
                                                <div className="h-1.5 bg-border rounded overflow-hidden mt-1">
                                                  <div
                                                    className="h-full bg-amber-500 rounded transition-[width]"
                                                    style={{ width: `${pct}%` }}
                                                  />
                                                </div>
                                                <div className="text-[10px] font-mono tabular-nums text-ctu-text-muted mt-0.5">
                                                  {ps.score.toFixed(2)}
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {/* Results */}
                                    {r.outcomes && r.outcomes.length > 0 && (
                                      <div>
                                        <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                                          {t('detail.researchers.outcomes')}
                                        </span>
                                        <div className="mt-2 overflow-x-auto rounded-md border border-ctu-border bg-background">
                                          <Table className="text-xs">
                                            <TableBody>
                                              {r.outcomes.map((o) => {
                                                const simSum = o.keywords
                                                  ? o.keywords.reduce(
                                                      (s, k) => s + k.similarity,
                                                      0,
                                                    )
                                                  : 0;
                                                return (
                                                  <TableRow
                                                    key={o.outcome_id}
                                                    className="hover:bg-muted/30"
                                                  >
                                                    <TableCell className="px-2 py-1.5 align-top whitespace-nowrap font-mono tabular-nums text-ctu-text-muted w-12">
                                                      {o.year ?? '—'}
                                                    </TableCell>
                                                    <TableCell className="px-2 py-1.5 align-top">
                                                      <button
                                                        type="button"
                                                        className="font-medium text-ctu-text hover:text-primary hover:underline text-left"
                                                        onClick={() =>
                                                          window.open(
                                                            `/outcome/${o.outcome_id}`,
                                                            '_blank',
                                                            'noopener,noreferrer',
                                                          )
                                                        }
                                                      >
                                                        {o.outcome_name}
                                                      </button>
                                                      {o.keywords && o.keywords.length > 0 && (
                                                        <div className="mt-1 flex flex-wrap gap-1">
                                                          {o.keywords.map((kw, ki) => (
                                                            <Badge
                                                              key={ki}
                                                              variant="warning"
                                                              className="px-1.5 py-0 text-[10px] font-medium"
                                                            >
                                                              {kw.keyword} ({kw.similarity.toFixed(2)})
                                                            </Badge>
                                                          ))}
                                                        </div>
                                                      )}
                                                    </TableCell>
                                                    <TableCell className="px-2 py-1.5 align-top text-right whitespace-nowrap w-24">
                                                      {o.type && (
                                                        <div className="text-[10px] text-ctu-text-muted">
                                                          {o.type}
                                                        </div>
                                                      )}
                                                      <span className="font-mono tabular-nums text-ctu-text-muted">
                                                        {simSum.toFixed(2)}
                                                      </span>
                                                    </TableCell>
                                                  </TableRow>
                                                );
                                              })}
                                            </TableBody>
                                          </Table>
                                        </div>
                                      </div>
                                    )}
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {sortedResearchers.length > RESEARCHERS_PER_PAGE && (
                  <div className="flex flex-col items-center gap-2 px-6 pt-2 sm:flex-row sm:justify-between">
                    <span className="text-xs text-ctu-text-muted tabular-nums">
                      {offset + 1}–
                      {Math.min(offset + RESEARCHERS_PER_PAGE, sortedResearchers.length)} /{' '}
                      {sortedResearchers.length}
                    </span>
                    <Pagination className="w-auto mx-0">
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            href="#"
                            aria-disabled={currentPage <= 1}
                            className={
                              currentPage <= 1
                                ? 'pointer-events-none opacity-40'
                                : ''
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage > 1) setPage(currentPage - 1);
                            }}
                          />
                        </PaginationItem>

                        {paginationItems().map((it, idx) =>
                          it === 'ellipsis' ? (
                            <PaginationItem key={`e-${idx}`}>
                              <PaginationEllipsis />
                            </PaginationItem>
                          ) : (
                            <PaginationItem key={it}>
                              <PaginationLink
                                href="#"
                                isActive={it === currentPage}
                                onClick={(e) => {
                                  e.preventDefault();
                                  setPage(it);
                                }}
                              >
                                {it}
                              </PaginationLink>
                            </PaginationItem>
                          ),
                        )}

                        <PaginationItem>
                          <PaginationNext
                            href="#"
                            aria-disabled={currentPage >= totalPages}
                            className={
                              currentPage >= totalPages
                                ? 'pointer-events-none opacity-40'
                                : ''
                            }
                            onClick={(e) => {
                              e.preventDefault();
                              if (currentPage < totalPages) setPage(currentPage + 1);
                            }}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </PageContent>
    </PageContainer>
  );
}
